import { useState } from "react";
import axios from "axios";
import { Download, Loader2 } from "lucide-react";
import { Logo } from "./Primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLS = ["created_at", "name", "email", "organization", "role", "message"];

const toCsv = (rows) => [COLS.join(","), ...rows.map((r) => COLS.map((c) => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");

export default function AdminWaitlist() {
  const [key, setKey] = useState("");
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/waitlist`, { headers: { "X-Admin-Key": key } });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e2) {
      setRows(null);
      setErr(e2?.response?.status === 401 ? "Invalid admin key." : "Couldn't load entries.");
    } finally { setLoading(false); }
  };

  const download = () => {
    const url = URL.createObjectURL(new Blob([toCsv(rows)], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "waitlist.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main data-testid="admin-waitlist-page" className="min-h-screen mx-auto max-w-6xl px-4 sm:px-6 py-12">
      <a href="/" className="flex items-center gap-2.5" data-testid="admin-home-link"><Logo className="h-8 w-8" /><span className="font-display text-lg font-bold">Morphogenesis</span></a>
      <h1 className="font-display mt-10 text-4xl font-extrabold tracking-tight">Contact inbox</h1>
      <form onSubmit={load} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl">
        <input type="password" required autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} placeholder="Admin key" data-testid="admin-key-input"
          className="flex-1 rounded-md border border-[var(--line)] bg-[var(--surface)] px-5 py-3 outline-none focus:border-[var(--teal)]" />
        <button type="submit" disabled={loading} data-testid="admin-load-btn" className="inline-flex items-center justify-center gap-2 rounded-md bg-[var(--teal)] px-6 py-3 font-semibold text-[var(--on-teal)] hover:bg-[var(--text)] transition-colors">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Load entries
        </button>
      </form>
      {err && <p data-testid="admin-error" className="mt-4 text-rose-700 dark:text-rose-300">{err}</p>}
      {rows && (
        <section className="mt-10">
          <div className="flex items-center justify-between">
            <p data-testid="admin-count" className="text-[var(--muted)]">{rows.length} {rows.length === 1 ? "entry" : "entries"}</p>
            <button onClick={download} disabled={!rows.length} data-testid="admin-csv-btn" className="inline-flex items-center gap-2 rounded-md border border-[var(--line)] bg-[var(--surface)] px-4 py-2 text-sm hover:border-[var(--teal)] hover:text-[var(--teal)] transition-colors disabled:opacity-50"><Download className="h-4 w-4" /> CSV</button>
          </div>
          <div className="mt-4 overflow-x-auto rounded-lg border border-[var(--line)] bg-[var(--surface)]">
            <table data-testid="admin-table" className="w-full text-sm text-left">
              <thead className="bg-[var(--surface)] font-mono text-xs uppercase tracking-wider text-[var(--muted)]">
                <tr>{["Joined", "Name", "Email", "Organization", "Role", "Message"].map((h) => <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-[var(--line)] align-top">
                    <td className="px-4 py-3 whitespace-nowrap text-[var(--muted)]">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3">{r.name}</td>
                    <td className="px-4 py-3"><a href={`mailto:${r.email}`} className="text-[var(--teal)]">{r.email}</a></td>
                    <td className="px-4 py-3">{r.organization}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{r.role}</td>
                    <td className="px-4 py-3 min-w-[16rem] text-[var(--muted)]">{r.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </main>
  );
}
